import {
  createIntent,
  type AdminSelf,
  type ApiClient,
  type operations,
  CommandIntent,
} from '@saay/api-client';
import {
  businessDayBounds,
  formatTimestamp,
  SOLAR_HIJRI_MONTHS_FA,
  timestampToBusinessDate,
  timestampToTehran,
  toPersianDigits,
} from '@saay/formatting';
import { useApiClient } from '@saay/web-app';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useRef } from 'react';

/** `GET /admin/me/sessions` (AC §27.1). */
export type AdminSessionList =
  operations['listAdminSessions']['responses'][200]['content']['application/json'];

export type AdminSession = AdminSessionList['items'][number];

/** `POST /admin/me/sessions/sign-out-others` (AC §27.1). */
export type SignOutOtherSessions =
  operations['signOutOtherAdminSessions']['responses'][200]['content']['application/json'];

const SESSIONS_KEY = ['admin', 'sessions'] as const;

const CHANNEL_LABEL_FA: Readonly<Record<string, string>> = {
  ADMIN_WEB: 'پنل مدیریت',
  BUYER_MOBILE: 'اپ موبایل',
  BUYER_WEB: 'وب',
  MERCHANT_WEB: 'پنل فروشگاه',
  OTHER: 'سایر',
};

/** Channel label. An unknown value stays neutral («سایر»). */
export function sessionChannelLabel(channel: string): string {
  return CHANNEL_LABEL_FA[channel] ?? 'سایر';
}

/**
 * AC §27.2: «تغییر رمز عبور» only when `/admin/me` says a password credential exists.
 * The generated `AdminSelf` does not list the flag yet; the field is `hasPassword`.
 */
export function selfHasPassword(self: AdminSelf): boolean {
  return (self as AdminSelf & { readonly hasPassword?: unknown }).hasPassword === true;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Last activity as a relative Solar Hijri time in Asia/Tehran: minutes and hours when recent,
 * otherwise «دیروز» or the Solar Hijri date.
 */
export function formatRelativeSolarHijri(value: string, nowMs: number): string {
  const at = Date.parse(value);
  if (Number.isNaN(at)) {
    return formatTimestamp(value);
  }
  const elapsed = Math.max(0, nowMs - at);
  const minute = 60_000;
  const hour = 60 * minute;
  if (elapsed < minute) {
    return 'همین حالا';
  }
  if (elapsed < hour) {
    return `${toPersianDigits(Math.floor(elapsed / minute))} دقیقه پیش`;
  }
  const nowIso = new Date(nowMs).toISOString();
  const tehran = timestampToTehran(value);
  const nowTehran = timestampToTehran(nowIso);
  const sameDay =
    tehran.year === nowTehran.year &&
    tehran.month === nowTehran.month &&
    tehran.day === nowTehran.day;
  if (sameDay) {
    return `${toPersianDigits(Math.floor(elapsed / hour))} ساعت پیش`;
  }
  const time = toPersianDigits(`${pad2(tehran.hour)}:${pad2(tehran.minute)}`);
  const today = timestampToBusinessDate(nowIso);
  const yesterday = timestampToBusinessDate(
    new Date(Date.parse(businessDayBounds(today).start) - 1).toISOString(),
  );
  if (timestampToBusinessDate(value) === yesterday) {
    return `دیروز، ${time}`;
  }
  const month = SOLAR_HIJRI_MONTHS_FA[tehran.month - 1] ?? '';
  const year = tehran.year === nowTehran.year ? '' : ` ${tehran.year}`;
  return toPersianDigits(
    `${tehran.day} ${month}${year}، ${pad2(tehran.hour)}:${pad2(tehran.minute)}`,
  );
}

export function signedOutOthersFa(count: number): string {
  if (count === 0) {
    return 'دستگاه دیگری خارج نشد.';
  }
  return `${toPersianDigits(count)} دستگاه دیگر خارج شد.`;
}

/** DELETE with the session's `Idempotency-Key` (AC §27.1), sent by the api-client from the intent. */
async function deleteWithKey(client: ApiClient, path: string, key: string): Promise<void> {
  await client.delete(path, { intent: new CommandIntent('session.end', () => key) });
}

export function useAdminSessions(): UseQueryResult<AdminSessionList> {
  const client = useApiClient();
  return useQuery<AdminSessionList, Error>({
    queryFn: async ({ signal }) =>
      (await client.get<AdminSessionList>('/admin/me/sessions', { signal })).data,
    queryKey: SESSIONS_KEY,
    // A 503 carries retryAfterSeconds 30; the screen offers the retry, it does not fire one itself.
    retry: false,
  });
}

export function useEndAdminSession(): UseMutationResult<void, Error, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const intents = useRef(new Map<string, CommandIntent>());
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const intent = intents.current.get(sessionId) ?? createIntent('admin.session.end');
      intents.current.set(sessionId, intent);
      await deleteWithKey(
        client,
        `/admin/me/sessions/${encodeURIComponent(sessionId)}`,
        intent.keyFor({ sessionId }),
      );
      intent.complete();
      intents.current.delete(sessionId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useSignOutOtherSessions(): UseMutationResult<SignOutOtherSessions, Error, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const intent = useRef(createIntent('admin.sessions.sign-out-others'));
  return useMutation({
    mutationFn: async () => {
      const result = await client.post<SignOutOtherSessions>('/admin/me/sessions/sign-out-others', {
        intent: intent.current,
      });
      intent.current.complete();
      intent.current = createIntent('admin.sessions.sign-out-others');
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}
